// ============================================================
// TMR Portal Admin — leituras dos contratos emitidos, sobre @tmr/api-client
// ============================================================
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { api } from '@tmr/api-client';
import { patientContractKeys } from '../queryClient';
import { exportPageReader } from '../../lib/export/exportPageReader';
import { detailRefreshInterval } from './status';
import type {
  PatientContractDetail,
  PatientContractDocument,
  PatientContractFilters,
  PatientContractPage,
  PatientContractRow,
} from './contract';

/** How many contracts a page holds. Matches the backend's default. */
export const PATIENT_CONTRACT_PAGE_SIZE = 25;

/**
 * A page of issued contracts, newest first.
 *
 * Paged, unlike the templates list: contracts are one per sale, forever.
 *
 * **Read again when the operator comes back to the tab.** Contracts are issued, signed and processed in
 * the v1.1, never here, so the list goes stale while the operator is somewhere else — typically on the
 * sale, watching a patient sign (`09` §11, N2).
 *
 * @param filters What to show.
 * @param enabled Whether the operator holds `patient.contracts.read`.
 */
export function usePatientContracts(
  filters: PatientContractFilters = {},
  enabled = true,
): UseQueryResult<PatientContractPage> {
  const query = { pageSize: PATIENT_CONTRACT_PAGE_SIZE, ...filters };

  return useQuery({
    queryKey: patientContractKeys.list(query),
    queryFn: ({ signal }) => api.get<PatientContractPage>('/patient-contracts', { query, signal }),
    enabled,
    refetchOnWindowFocus: true,
    // The previous answer stays on screen while the next page or filter loads — the table does not
    // collapse to a skeleton on every keystroke, and the totals above it, which do not depend on the
    // filter, do not flicker to dashes.
    placeholderData: (previous) => previous,
  });
}

/**
 * Reads the list for **export**, a thousand rows at a time.
 *
 * Its own route rather than the list with a bigger page: only something that declared itself an export
 * reaches the larger ceiling on the backend (20 §3).
 *
 * @param filters The screen's active filters, exactly as the list itself sends them.
 */
export function patientContractExportPage(filters: PatientContractFilters = {}) {
  return exportPageReader<PatientContractRow>('/patient-contracts/export', {
    status: filters.status,
    issuedFrom: filters.issuedFrom,
    issuedTo: filters.issuedTo,
    search: filters.search,
  });
}

/**
 * One contract with its signatories, evidence and timeline.
 *
 * **It reads itself again while something is still to happen** — every 15 s while the patient has not
 * signed, every minute while the signed PDF is being produced — and stops once the contract is settled
 * (`detailRefreshInterval`). The operator who opens this panel is usually waiting on exactly that, and a
 * refresh button would make them the polling loop.
 *
 * @param documentId The contract, or undefined while no panel is open.
 */
export function usePatientContract(
  documentId: string | undefined,
): UseQueryResult<PatientContractDetail> {
  return useQuery({
    queryKey: patientContractKeys.detail(documentId ?? ''),
    queryFn: ({ signal }) =>
      api.get<PatientContractDetail>(`/patient-contracts/${documentId}`, { signal }),
    enabled: Boolean(documentId),
    refetchInterval: (query) => detailRefreshInterval(query.state.data),
    refetchOnWindowFocus: true,
  });
}

/**
 * The frozen text of an issued contract — the words the patient read.
 *
 * Fetched only when the reading view is opened: it is a whole contract, and the detail panel is opened far
 * more often than the text is read.
 *
 * @param documentId The contract, or undefined while the reading view is closed.
 */
export function usePatientContractDocument(
  documentId: string | undefined,
): UseQueryResult<PatientContractDocument> {
  return useQuery({
    queryKey: patientContractKeys.document(documentId ?? ''),
    queryFn: ({ signal }) =>
      api.get<PatientContractDocument>(`/patient-contracts/${documentId}/document`, { signal }),
    enabled: Boolean(documentId),
  });
}

/**
 * Asks the v1.1 routine to try this contract's processing again.
 *
 * Invalidates the contract afterwards but not immediately usefully: the retry happens on the legacy's next
 * cycle, so what changes right away is only that something is queued. The outcome arrives later, as the
 * legacy's own events on the document's chain.
 */
export function useReprocessPatientContract(): UseMutationResult<
  { documentId: string },
  Error,
  string
> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (documentId) =>
      api.post<{ documentId: string }>(`/patient-contracts/${documentId}/reprocessing`, {}),
    onSuccess: (_, documentId) => {
      void client.invalidateQueries({ queryKey: patientContractKeys.detail(documentId) });
      void client.invalidateQueries({ queryKey: patientContractKeys.lists() });
    },
  });
}

/**
 * Where the signed PDF is served from.
 *
 * A URL rather than a fetch, because the browser downloads it: the endpoint answers with `attachment`, and
 * the session cookie is same-origin, so an anchor is all it takes. **Never a storage URL and never a SAS**
 * — the link is to this API, which checks the permission on every request (`24` §13).
 *
 * @param documentId The contract.
 */
export function patientContractFileUrl(documentId: string): string {
  return `/api/v1/patient-contracts/${documentId}/file`;
}

/**
 * Where a signatory's drawn signature is served from.
 *
 * @param signatoryId The signatory.
 */
export function patientContractSignatureUrl(signatoryId: string): string {
  return `/api/v1/patient-contracts/signatories/${signatoryId}/signature`;
}
